import React, { useEffect, useMemo, useState } from "react";
import { Form, Row, Col, Modal, HelpBlock, FormGroup, ControlLabel } from "react-bootstrap";
import Card from "components/Card/Card.jsx";
import Button from "components/CustomButton/CustomButton";
import "./AddAppointment.scss";
import { useForm } from "react-hook-form";
import MomentUtils from "@date-io/moment";
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import moment from "moment-timezone";
import { getDurations } from "helpers/reservation.helper";
import { filterActiveServices, formatServiceDuration, sortServices } from "helpers/appointmentService.helper";
import {
  durationOptionsForService,
  findAppointmentConflict,
  resolveAppointmentDuration,
  splitResourcesByService
} from "helpers/appointmentAdmin.helper";
import { Business } from "types/business";
import { formatPhone } from "helpers/phone.helper";
import {
  BuildCusmomizableInputsForm,
  getInputFields
} from "components/Settings/CustomizableInputFiels/BuildCustomizableInputFields";
import { CustomizableInputFieldType } from "types/customizable-input-fields";
import { useMediaQuery } from "hooks/useMediaQuery";
import { handleEnterKeyNextField } from "helpers/form.helper";

// Staff-side appointment booking (plan §7, phase 6).
//
// A single form rather than the customer flow's step machine: staff already know what
// they are booking and are usually on the phone while they do it, so stepping through
// service -> resource -> day -> slot would be slower than the call it replaces. The
// staff-mode adjustments §7 asks for follow from that — any resource is selectable,
// duration is overridable, and no lead time applies, because this writes to
// `reservations` directly rather than going through the slot engine.

export type AddAppointmentForm = {
  name: string;
  phone?: string;
  email?: string;
  note?: string;
  serviceId?: string;
  resourceId?: string;
  duration?: any;
  date?: Date | string;
  /**
   * Which guest fields this modal actually put on screen.
   *
   * The customizable-fields config decides that per business, so a form that never
   * rendered an email input submits `email: undefined` — indistinguishable from a
   * staff member clearing one. Without this the edit path would delete a stored value
   * every time an appointment was saved, which is exactly what the restaurant modal
   * does to `note` today. The caller clears only what is listed here.
   */
  collectedFields?: string[];
};

// Party size is fixed at 1 for appointments (plan §0); `service` here is the restaurant
// preference list, unrelated to the appointments services subcollection this modal has
// its own picker for; order numbers belong to the waitlist flow.
const EXCLUDED_FIELDS = [
  CustomizableInputFieldType.PARTY_SIZE,
  CustomizableInputFieldType.SERVICE,
  CustomizableInputFieldType.ORDER_NUMBER
];

// Customizable-field key -> the reservation document field it writes. `phoneNumber`
// renders an input named `phone`, hence the remap.
const GUEST_FIELD_TO_PAYLOAD = {
  name: "name",
  phoneNumber: "phone",
  email: "email"
};

type AddAppointmentModalProps = {
  showModal: boolean;
  handleClose: (form?: AddAppointmentForm) => void;
  appointment;
  preselectedResourceId?: string;
  services: any[];
  resources: any[];
  reservations: any[];
  selectedDate;
  handleDateChange;
  timezone: string;
  business: Business;
  resourceLabel: { singular: string; plural: string };
};

function AddAppointmentModal(props: AddAppointmentModalProps) {
  const {
    showModal,
    handleClose,
    appointment,
    preselectedResourceId,
    services,
    resources,
    reservations,
    selectedDate,
    handleDateChange,
    timezone,
    business,
    resourceLabel
  } = props;
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isEditing = !!appointment;

  moment.tz.setDefault(timezone);

  const { register, handleSubmit, errors } = useForm<AddAppointmentForm>();

  const existing = isEditing ? appointment.data() : null;

  // Editing opens on the appointment's own time, not on whatever day the list is
  // filtered to. The date lives in the parent because the parent also uses it to reload
  // the day's reservations behind the modal, which is what feeds the conflict check.
  useEffect(() => {
    if (existing?.reservationTime) {
      handleDateChange(moment(existing.reservationTime.toDate()).tz(timezone));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment]);

  const [form, setForm] = useState<AddAppointmentForm>({
    name: existing?.name || "",
    phone: existing?.phone || "",
    email: existing?.email || "",
    note: existing?.note || "",
    serviceId: existing?.serviceId || "",
    resourceId: existing?.resourceId || preselectedResourceId || "",
    duration: existing?.duration ? String(existing.duration) : ""
  });

  // Only bookable services are offered. A service switched off stays on an existing
  // appointment — it is a record of what happened — but cannot be chosen afresh.
  const bookableServices = useMemo(() => sortServices(filterActiveServices(services)), [services]);
  const selectedService = useMemo(
    () => services.find(service => service.id === form.serviceId),
    [services, form.serviceId]
  );

  // Resources that perform the chosen service come first; the rest stay selectable
  // below it, because §7 lets staff put anyone on anything.
  const { performing, others } = useMemo(
    () => splitResourcesByService(resources, form.serviceId),
    [resources, form.serviceId]
  );

  const durationOptions = useMemo(
    () => durationOptionsForService(getDurations(15), selectedService),
    [selectedService]
  );

  // Picking a service sets the duration to that service's own, unless the staff member
  // has already typed an override — theirs wins, per §7.
  const [durationTouched, setDurationTouched] = useState(isEditing);
  useEffect(() => {
    if (durationTouched || !selectedService?.duration) {
      return;
    }
    setForm(prev => ({ ...prev, duration: String(selectedService.duration) }));
  }, [selectedService, durationTouched]);

  const startDate = moment.isMoment(selectedDate) ? selectedDate.toDate() : selectedDate;
  const conflict = findAppointmentConflict({
    reservations,
    resourceId: form.resourceId,
    start: startDate,
    duration: resolveAppointmentDuration(form.duration, selectedService),
    timezone,
    ignoreId: appointment?.id
  });

  const changeDate = date => handleDateChange(date.tz(timezone));

  // Which guest inputs this business's field config actually rendered — see
  // AddAppointmentForm.collectedFields for why the caller needs to know.
  const collectedFields = useMemo(
    () =>
      getInputFields(business, "reservations", EXCLUDED_FIELDS)
        .flat()
        .map(field => GUEST_FIELD_TO_PAYLOAD[field.name])
        .filter(Boolean),
    [business]
  );

  const onSubmit = data => {
    handleClose({
      ...data,
      // The selects are controlled, so `form` is the source of truth for them —
      // react-hook-form only sees what the registered inputs happen to hold.
      serviceId: form.serviceId,
      resourceId: form.resourceId,
      duration: resolveAppointmentDuration(form.duration, selectedService),
      phone: formatPhone(data.phone),
      date: selectedDate,
      collectedFields
    });
  };

  const closeModal = () => handleClose();

  const resourceNoun = (resourceLabel?.singular || "Resource").toUpperCase();
  const conflictData = conflict ? (typeof conflict.data === "function" ? conflict.data() : conflict) : null;

  return (
    <Modal enforceFocus={false} dialogClassName="add-appointment-modal" show={showModal} onHide={closeModal}>
      {isMobile && (
        <Button className="sign-up-modal-wrapper__close-btn" simple onClick={closeModal}>
          <i className="fa fa-times" />
        </Button>
      )}
      <Card
        title={isEditing ? "Edit Appointment" : "Add Appointment"}
        textCenter={true}
        content={
          <Form
            noValidate
            className="add-appointment"
            onSubmit={handleSubmit(onSubmit)}
            onKeyDown={handleEnterKeyNextField}
          >
            <Row>
              <Col md={12}>
                <FormGroup>
                  <ControlLabel>DATE/TIME</ControlLabel>
                  <MuiPickersUtilsProvider utils={MomentUtils}>
                    <DateTimePicker
                      variant="inline"
                      autoOk
                      value={selectedDate}
                      minutesStep={5}
                      className="reservation-datepicker"
                      onChange={changeDate}
                    />
                  </MuiPickersUtilsProvider>
                </FormGroup>
              </Col>
            </Row>

            {bookableServices.length ? (
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <ControlLabel>SERVICE</ControlLabel>
                    <select
                      className="form-control"
                      name="serviceId"
                      value={form.serviceId || ""}
                      onChange={e => setForm(prev => ({ ...prev, serviceId: e.target.value }))}
                    >
                      <option value="">No service</option>
                      {bookableServices.map(service => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                          {formatServiceDuration(service.duration)
                            ? ` — ${formatServiceDuration(service.duration)}`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </FormGroup>
                </Col>
              </Row>
            ) : null}

            {resources.length ? (
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <ControlLabel>{resourceNoun}</ControlLabel>
                    <select
                      className="form-control"
                      name="resourceId"
                      value={form.resourceId || ""}
                      onChange={e => setForm(prev => ({ ...prev, resourceId: e.target.value }))}
                    >
                      <option value="">Unassigned</option>
                      {performing.map(resource => (
                        <option key={resource.id} value={resource.id}>
                          {resource.name}
                        </option>
                      ))}
                      {others.length ? (
                        <optgroup label="Does not perform this service">
                          {others.map(resource => (
                            <option key={resource.id} value={resource.id}>
                              {resource.name}
                            </option>
                          ))}
                        </optgroup>
                      ) : null}
                    </select>
                    {conflictData && (
                      <HelpBlock className="add-appointment__conflict">
                        Already booked at this time
                        {conflictData.name ? ` — ${conflictData.name}` : ""}. You can still save this.
                      </HelpBlock>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            ) : null}

            <Row>
              <Col md={12}>
                <FormGroup validationState={(!!errors.duration && "error") || null}>
                  <ControlLabel>DURATION</ControlLabel>
                  <select
                    className="form-control"
                    name="duration"
                    value={form.duration || ""}
                    onChange={e => {
                      setDurationTouched(true);
                      setForm(prev => ({ ...prev, duration: e.target.value }));
                    }}
                  >
                    <option value="">Select Duration</option>
                    {durationOptions.map((duration, index) => (
                      <option key={index} value={duration.value}>
                        {duration.label}
                      </option>
                    ))}
                  </select>
                </FormGroup>
              </Col>
            </Row>

            {/* Party size is fixed at 1 for appointments (plan §0), so its input is
                suppressed rather than shown with a value staff cannot meaningfully change. */}
            <BuildCusmomizableInputsForm<AddAppointmentForm>
              form={form}
              register={register}
              errors={errors}
              business={business}
              setForm={setForm}
              type="reservations"
              exclude={EXCLUDED_FIELDS}
              alwaysNextBtn
            />

            <Row>
              <Col md={12}>
                <div className="add-appointment__actions">
                  <Button
                    onClick={closeModal}
                    className="add-appointment__cancel"
                    bsStyle="info"
                    pullRight
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button bsStyle="info" pullRight fill type="submit">
                    {isEditing ? "Save Appointment" : "Add Appointment"}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        }
      ></Card>
    </Modal>
  );
}

export default AddAppointmentModal;
